/**
 * Device Fingerprint Utility
 * 
 * Creates a unique fingerprint for the current device/browser
 * Used for:
 * - Storing device-specific credentials (biometric/passkey)
 * - Remembering accounts per device
 * - Session device tracking
 */

// Simple hash function that works in non-secure contexts (fallback for HTTP)
function simpleHash(str: string): string {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	// Convert to hex and pad to ensure consistent length
	const hex = Math.abs(hash).toString(16).padStart(8, '0');
	// Create a longer hash by hashing different parts
	let result = '';
	for (let i = 0; i < 4; i++) {
		let subHash = 0;
		const start = Math.floor((str.length / 4) * i);
		const end = Math.floor((str.length / 4) * (i + 1));
		const part = str.substring(start, end) + i.toString();
		for (let j = 0; j < part.length; j++) {
			const char = part.charCodeAt(j);
			subHash = ((subHash << 5) - subHash) + char;
			subHash = subHash & subHash;
		}
		result += Math.abs(subHash).toString(16).padStart(8, '0');
	}
	return hex + result;
}

// Generate a hash from a string
async function hashString(str: string): Promise<string> {
	// Check if crypto.subtle is available (only in secure contexts)
	if (typeof crypto !== 'undefined' && crypto.subtle) {
		try {
			const encoder = new TextEncoder();
			const data = encoder.encode(str);
			const hashBuffer = await crypto.subtle.digest("SHA-256", data);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
		} catch {
			// Fall through to simple hash
		}
	}
	// Fallback for non-secure contexts (HTTP)
	return simpleHash(str);
}

// Get browser and platform info
function getBrowserInfo(): Record<string, string | number | boolean> {
	if (typeof window === "undefined") {
		return {};
	}
	
	return {
		userAgent: navigator.userAgent,
		language: navigator.language,
		languages: navigator.languages.join(","),
		platform: navigator.platform,
		cookiesEnabled: navigator.cookieEnabled,
		doNotTrack: navigator.doNotTrack || "",
		hardwareConcurrency: navigator.hardwareConcurrency || 0,
		maxTouchPoints: navigator.maxTouchPoints || 0,
		deviceMemory: (navigator as unknown as { deviceMemory?: number }).deviceMemory || 0,
	};
}

// Get screen info
function getScreenInfo(): Record<string, number | string> {
	if (typeof window === "undefined") {
		return {};
	}
	
	return {
		screenWidth: screen.width,
		screenHeight: screen.height,
		screenAvailWidth: screen.availWidth,
		screenAvailHeight: screen.availHeight,
		colorDepth: screen.colorDepth,
		pixelDepth: screen.pixelDepth,
		devicePixelRatio: window.devicePixelRatio || 1,
	};
}

// Get timezone info
function getTimezoneInfo(): Record<string, string | number> {
	const date = new Date();
	return {
		timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		timezoneOffset: date.getTimezoneOffset(),
	};
}

// Get canvas fingerprint
async function getCanvasFingerprint(): Promise<string> {
	if (typeof document === "undefined") {
		return "";
	}
	
	try {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		if (!ctx) return "";
		
		canvas.width = 200;
		canvas.height = 50;
		
		// Draw unique pattern
		ctx.textBaseline = "top";
		ctx.font = "14px Arial";
		ctx.fillStyle = "#f60";
		ctx.fillRect(125, 1, 62, 20);
		ctx.fillStyle = "#069";
		ctx.fillText("VastiPost FP", 2, 15);
		ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
		ctx.fillText("VastiPost FP", 4, 17);
		
		return canvas.toDataURL();
	} catch {
		return "";
	}
}

// Get WebGL fingerprint
function getWebGLFingerprint(): string {
	if (typeof document === "undefined") {
		return "";
	}
	
	try {
		const canvas = document.createElement("canvas");
		const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
		if (!gl) return "";
		
		const webgl = gl as WebGLRenderingContext;
		
		const debugInfo = webgl.getExtension("WEBGL_debug_renderer_info");
		if (!debugInfo) return "";
		
		const vendor = webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || "";
		const renderer = webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "";
		
		return `${vendor}~${renderer}`;
	} catch {
		return "";
	}
}

// Storage key for cached fingerprint
const FINGERPRINT_KEY = "vp_device_fp";

/**
 * Get or generate device fingerprint
 * Caches in localStorage for consistency
 */
export async function getDeviceFingerprint(): Promise<string> {
	// Check for cached fingerprint
	if (typeof localStorage !== "undefined") {
		const cached = localStorage.getItem(FINGERPRINT_KEY);
		if (cached) {
			return cached;
		}
	}
	
	// Collect fingerprint components
	const components = {
		browser: getBrowserInfo(),
		screen: getScreenInfo(),
		timezone: getTimezoneInfo(),
		canvas: await getCanvasFingerprint(),
		webgl: getWebGLFingerprint(),
	};
	
	// Create a stable string representation
	const fingerprintData = JSON.stringify(components);
	
	// Hash the fingerprint data
	const fingerprint = await hashString(fingerprintData);
	
	// Cache in localStorage
	if (typeof localStorage !== "undefined") {
		localStorage.setItem(FINGERPRINT_KEY, fingerprint);
	}
	
	return fingerprint;
}

/**
 * Clear cached fingerprint
 * Useful when user wants to "forget" this device
 */
export function clearDeviceFingerprint(): void {
	if (typeof localStorage !== "undefined") {
		localStorage.removeItem(FINGERPRINT_KEY);
	}
}

/**
 * Get a human-readable device name
 */
export function getDeviceName(): string {
	if (typeof navigator === "undefined") {
		return "Unknown Device";
	}
	
	const ua = navigator.userAgent;
	
	// Mobile devices
	if (/iPhone/.test(ua)) return "iPhone";
	if (/iPad/.test(ua)) return "iPad";
	if (/Android.*Mobile/.test(ua)) return "Android Phone";
	if (/Android/.test(ua)) return "Android Tablet";
	
	// Desktop browsers
	if (/Windows/.test(ua)) {
		if (/Edge/.test(ua)) return "Windows (Edge)";
		if (/Chrome/.test(ua)) return "Windows (Chrome)";
		if (/Firefox/.test(ua)) return "Windows (Firefox)";
		return "Windows";
	}
	if (/Macintosh/.test(ua)) {
		if (/Safari/.test(ua) && !/Chrome/.test(ua)) return "Mac (Safari)";
		if (/Chrome/.test(ua)) return "Mac (Chrome)";
		if (/Firefox/.test(ua)) return "Mac (Firefox)";
		return "Mac";
	}
	if (/Linux/.test(ua)) {
		if (/Chrome/.test(ua)) return "Linux (Chrome)";
		if (/Firefox/.test(ua)) return "Linux (Firefox)";
		return "Linux";
	}
	
	return "Unknown Device";
}
